/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, eqeqeq, no-lone-blocks */
import logger from '../utils/logger';
import React, { useEffect, useState } from 'react';
import '../css/VersionPopup.css';
import { versionlist, ReplaceVersion, ImageBaseUrl, api } from '../api/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CustomAlert from './CustomAlert';
import { DefaultemailIcon, DefaultUserIcon, PlusIcon } from './Icon';
import { useTranslation } from '../hooks/useTranslation';
import { useLangMap } from '../hooks/useLangMap';
import TranslationTextAreaPopup from '../hooks/TranslationTextAreaPopup';
import { useFetchVersions } from '../hooks/useFetchVersions';
import { getLevelKey } from '../utils/getLevel';
import { stripHtml } from '../components/StripHtml';

const VersionPopup = ({
  processId,
  currentLevel,
  currentParentId,
  onClose,
  viewVersion,
  LoginUser,
  title,
  handleSaveVersionDetails,
  status,
  type,
  versionPopupPayload,
  supportedLanguages,
  selectedLanguage,
}) => {
  const [activeTab, setActiveTab] = useState('contact');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revisionText, setRevisionText] = useState('');

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [emailList, setEmailList] = useState([]);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedUsers, setTempSelectedUsers] = useState([]);

  const [selectedUsers, setSelectedUsers] = useState({
    domain_owner: [],
    owner: [],
    architecture: [],
    manager: [],
    modeler: [],
  });
  const [showTranslationPopup, setShowTranslationPopup] = useState(false);
  const [translations, setTranslations] = useState({});
  const langMap = useLangMap(); // same hook used in first component

  const t = useTranslation();
  const { responseData, refetch } = useFetchVersions({
    processId,
    currentLevel,
    currentParentId,
    LoginUser,
    status,
  });

  useEffect(() => {
    if (versionPopupPayload) {
      setSelectedUsers(
        versionPopupPayload.contact_info || {
          domain_owner: [],
          owner: [],
          architecture: [],
          manager: [],
          modeler: [],
        }
      );
      // 🧠 Load revision translations
      const revisionData = versionPopupPayload.revision_info;
      if (typeof revisionData === 'object') {
        setTranslations(revisionData);
        const langKey = langMap[selectedLanguage] || 'EN';
        setRevisionText(revisionData[langKey]?.content || '');
      } else {
        setRevisionText(revisionData || '');
      }
    }
    setLoading(false);
  }, [versionPopupPayload, selectedLanguage, langMap]);

  useEffect(() => {
    if (!responseData || !langMap || Object.keys(langMap).length === 0) return;

    // Versions
    setVersions(responseData.versions || []);

    // Assigned Users List
    const users = responseData.assigned_users || [];
    setAssignedUsers(users);
    setEmailList(users.map((u) => u.user.email));

    // If popup data already loaded, skip overwriting
    if (versionPopupPayload) return;

    // contact_info
    setSelectedUsers(
      responseData.contact_info || {
        domain_owner: [],
        owner: [],
        architecture: [],
        manager: [],
        modeler: [],
      }
    );

    // revision_info
    let revisionData = {};
    try {
      revisionData =
        typeof responseData.revision_info === 'string'
          ? JSON.parse(responseData.revision_info)
          : responseData.revision_info || {};
    } catch (e) {
      logger.error('revision_info parse error', e);
    }

    setTranslations(revisionData);

    const langKey = langMap[selectedLanguage];
    setRevisionText(revisionData?.[langKey]?.content || '');
  }, [responseData, langMap, selectedLanguage, versionPopupPayload]);

  // ➕ Popup open
  const handleAddUser = (role) => {
    setCurrentRole(role);
    setSearchQuery('');
    setTempSelectedUsers(selectedUsers[role] || []);
    setShowEmailPopup(true);
  };

  // ✅ Select/Deselect email (Temporary) - Single selection only
  const selectUser = (userId) => {
    setTempSelectedUsers([userId]);
  };

  const handleAssign = () => {
    const selectedId = tempSelectedUsers[0];
    if (!selectedId) return;

    setSelectedUsers((prev) => {
      let currentList = prev[currentRole] || [];

      // Multi-select roles (Added one-by-one)
      if (currentRole === 'architecture' || currentRole === 'manager') {
        return {
          ...prev,
          [currentRole]: currentList.includes(selectedId)
            ? currentList
            : [...currentList, selectedId],
        };
      }
      // Single-select roles
      else {
        return {
          ...prev,
          [currentRole]: [selectedId],
        };
      }
    });
    setShowEmailPopup(false);
  };

  const handleAssignMyself = () => {
    const userId = LoginUser?.id;
    if (!userId) {
      CustomAlert.warning(
        t('warning') || 'Warning',
        t('user_session_not_found') || 'User session not found.'
      );
      return;
    }

    setSelectedUsers((prev) => {
      let currentList = prev[currentRole] || [];

      if (currentRole === 'architecture' || currentRole === 'manager') {
        return {
          ...prev,
          [currentRole]: currentList.includes(userId) ? currentList : [...currentList, userId],
        };
      } else {
        return {
          ...prev,
          [currentRole]: [userId],
        };
      }
    });
    setShowEmailPopup(false);
  };

  // ❌ Remove user
  const removeUser = (role, userId) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [role]: prev[role].filter((id) => id !== userId),
    }));
  };

  // 💾 Save
  const handleSave = async (tab) => {
    try {
      const levelParam = getLevelKey(currentLevel, currentParentId);

      const payload = {
        process_id: processId,
        level: levelParam,
        parent_id: currentParentId,
        contact_info: selectedUsers,
        revision_info: translations,
      };

      if (payload.contact_info || payload.revision_info) {
        handleSaveVersionDetails(payload);
      } else {
        CustomAlert.info(
          t('information') || 'Information',
          t('nothing_to_save_for_this_tab') || 'Nothing to save for this tab.'
        );
      }
    } catch (error) {
      CustomAlert.error(t('error') || 'Error', t('error_saving_data') || 'Error saving data.');
      logger.error('Save failed:', error);
    }
  };

  // 🔄 Replace Version
  const ReplaceVersionpage = async (level, processId, version) => {
    try {
      const response = await ReplaceVersion(level, processId, version);
      if (response && response.message) {
        CustomAlert.success('Success', response.message);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      CustomAlert.error(
        t('error') || 'Error',
        t('failed_to_replace_version') || 'Failed to replace version.'
      );
    }
  };

  const handleReplaceClick = (level, processId, version) => {
    CustomAlert.confirm(
      `${t('are_you_sure')}?`,
      `${t('this_will_replace_the_version_this_action_cannot_be_undone')}`,
      () => ReplaceVersionpage(level, processId, version),
      () => {},
      t('yes_confirm'),
      t('Cancel')
    );
  };

  const handleDownloadTranslations = async () => {
    try {
      const levelParam = getLevelKey(currentLevel, currentParentId);
      const response = await api.get('/models/export-translations', {
        params: {
          process_id: processId,
          level: levelParam,
          parent_id: currentParentId,
        },
        responseType: 'blob',
      });

      const pageGroupId = responseData?.page_group_id || processId || '';
      const cleanTitle = stripHtml(title).trim();
      const safeTitle = (cleanTitle || 'model').replace(/[/\\?%*:|"<>\r\n]/g, '_');
      const filename = `${pageGroupId} - ${safeTitle} - Translation.csv`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      CustomAlert.error(
        t('error') || 'Error',
        t('failed_to_download_translation_report') || 'Failed to download translation report.'
      );
      logger.error('Download failed:', error);
    }
  };

  const handleImportTranslations = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileInput = e.target;

    CustomAlert.confirm(
      t('confirm_import') || 'Confirm Import',
      t('are_you_sure_you_want_to_import_translations_from_this_file') ||
        'Are you sure you want to import translations from this file? This will replace the current draft translations.',
      async () => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('process_id', processId);
        formData.append('level', getLevelKey(currentLevel, currentParentId));
        formData.append('parent_id', currentParentId || '');

        try {
          await api.post('/models/import-translations', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          sessionStorage.setItem('import_success', 'true');
          fileInput.value = '';
          window.location.reload();
        } catch (error) {
          fileInput.value = '';
          CustomAlert.error(
            t('error') || 'Error',
            error.response?.data?.error ||
              t('failed_to_import_translations') ||
              'Failed to import translations.'
          );
          logger.error('Import failed:', error);
        }
      },
      () => {
        fileInput.value = '';
      },
      t('yes_confirm') || 'Yes, confirm',
      t('Cancel') || 'Cancel'
    );
  };

  const filteredUsers =
    searchQuery.trim() === ''
      ? []
      : assignedUsers
          .filter((u) => {
            if (currentRole === 'modeler') {
              const isModeler = u.role_id === 1;
              const isSuperAdmin = u.status === 'self';

              return isModeler || isSuperAdmin; // ⭐ sirf Modeler + Super Admin
            }
            return true;
          })
          .filter((u) =>
            `${u.user.first_name} ${u.user.last_name} ${u.user.email}`
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          );
  // Roles config (different for map vs swimlane)
  const roleBlocks =
    type === 'ProcessMaps'
      ? ['domain_owner', 'owner', 'modeler'] // Only Domain Owner + Process Owner
      : ['owner', 'architecture', 'manager', 'modeler']; // Swimlane

  const getRoleLabel = (role) => {
    switch (role) {
      case 'domain_owner':
        return t('process_domain_owner');
      case 'owner':
        return t('process_owner');
      case 'architecture':
        return t('process_architects');
      case 'manager':
        return t('process_manager');
      case 'modeler':
        return t('process_modeler');
      default:
        return '';
    }
  };
  const cleanLabel = (label) => label?.replace(/:$/, '');

  return (
    <>
      <div className="version-popup-overlay">
        <div className="version-popup">
          {/* Header */}
          <div className="popup-header modal_main">
            <h3>{stripHtml(title)}</h3>
            <div className="popup-actions">
              <button className="popup-button cancel" onClick={onClose}>
                {t('Cancel')}
              </button>
              <button className="popup-button save" onClick={() => handleSave(activeTab)}>
                {t('Save')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="tab-buttons">
            <button
              onClick={() => setActiveTab('contact')}
              className={activeTab === 'contact' ? 'active' : ''}
            >
              {t('contact')}
            </button>
            <button
              onClick={() => setActiveTab('revision')}
              className={activeTab === 'revision' ? 'active' : ''}
            >
              {t('revision_info')}
            </button>
            <button
              onClick={() => setActiveTab('version')}
              className={activeTab === 'version' ? 'active' : ''}
            >
              {t('version')}
            </button>
            <button
              onClick={() => setActiveTab('translation')}
              className={activeTab === 'translation' ? 'active' : ''}
            >
              {t('translation_management')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* CONTACT TAB */}
            {activeTab === 'contact' && (
              <div className="contact-tab">
                {roleBlocks.map((role) => {
                  const roleUsers = assignedUsers
                    .filter((user) => (selectedUsers[role] || []).includes(user.user.id))

                    .sort((a, b) => a.user.last_name.localeCompare(b.user.last_name));

                  return (
                    <div key={role} className="contact-item">
                      <div className="flex_full">
                        <label>
                          {getRoleLabel(role)}
                          {/* {role === "domain_owner"
                            ? t("process_domain_owner")
                            : role === "owner"
                              ? t("process_owner")
                              : role === "architecture"
                                ? t("process_architects")
                                : role === "manager"
                                  ? t("process_manager")
                                  : role === "modeler"
                                    ? t("process_modeler")   // ⭐ नया Label
                                    : ""} */}
                        </label>
                      </div>

                      {roleUsers.length > 0
                        ? roleUsers.map((roleUser, index) => (
                            <div key={index} className="owner_details_list">
                              <div className="owner_details">
                                <div className="owner-pic">
                                  {roleUser.user?.image ? (
                                    <>
                                      <img
                                        src={
                                          roleUser?.user.image.startsWith('http')
                                            ? roleUser?.user.image // ✅ Google ka full URL
                                            : `${ImageBaseUrl}uploads/profile_images/${roleUser.user.image}` // ✅ Local image
                                        }
                                        alt="Profile"
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          borderRadius: '50%',
                                          objectFit: 'cover',
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          const fallback = e.target.nextSibling;
                                          if (fallback) fallback.style.display = 'inline-block';
                                        }}
                                      />
                                      <span style={{ display: 'none' }}>
                                        <DefaultUserIcon />
                                      </span>
                                    </>
                                  ) : (
                                    <DefaultUserIcon />
                                  )}
                                </div>
                                <div className="owner-desc">
                                  <span className="owner-name">
                                    {roleUser.user.first_name} {roleUser.user.last_name}
                                  </span>
                                  <div className="owner-actions owner-flex">
                                    <a
                                      href={`mailto:${roleUser.user.email}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <DefaultemailIcon />
                                    </a>

                                    <button
                                      style={{ marginLeft: 20 }}
                                      className="popup-button remove"
                                      onClick={() => removeUser(role, roleUser.user.id)}
                                    >
                                      {t('REMOVE')}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        : null}

                      {/* ➕ Add button (hide if owner/domain_owner already assigned) */}

                      {!loading && // ⏳ Wait until API data loaded
                        !(role === 'owner' && roleUsers.length === 1) &&
                        !(role === 'modeler' && roleUsers.length === 1) &&
                        !(role === 'domain_owner' && roleUsers.length === 1) && (
                          <div className="owner_details_list">
                            <div className="owner_details">
                              <div className="owner-pic" onClick={() => handleAddUser(role)}>
                                <PlusIcon />
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* REVISION TAB */}
            {activeTab === 'revision' && (
              <div className="revision-tab">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <h4 style={{ margin: 0 }}>{t('revision_info')}</h4>
                  <button
                    className="popup-button"
                    style={{ backgroundColor: '#d9d9d9', color: '#000' }}
                    onClick={() => setShowTranslationPopup(true)}
                  >
                    {t('translation')}
                  </button>
                </div>

                <ReactQuill
                  value={revisionText}
                  onChange={(value) => {
                    setRevisionText(value);
                    const langKey = langMap[selectedLanguage] || 'EN';
                    setTranslations((prev) => ({
                      ...prev,
                      [langKey]: {
                        ...(typeof prev[langKey] === 'object' && prev[langKey] !== null
                          ? prev[langKey]
                          : {}),
                        content: value,
                      },
                    }));
                  }}
                  modules={{
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  placeholder="Write revision information..."
                />
              </div>
            )}

            {/* VERSION TAB */}
            {activeTab === 'version' && (
              <div className="version-tab" style={{ maxHeight: '430px', overflow: 'auto' }}>
                {loading ? (
                  <p>{t('loading')}</p>
                ) : versions.length === 0 ? (
                  <p>{t('no_version_history_found')}</p>
                ) : (
                  <table className="version-table">
                    <thead>
                      <tr>
                        <th>{t('version')}</th>
                        <th>{t('type')}</th>
                        <th>{t('status')}</th>

                        <th>{t('date_time')}</th>
                        <th>{t('user')}</th>
                        <th>{t('Action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((version, index) => (
                        <tr key={index}>
                          <td>{version.version}</td>
                          <td>{version.change_type || '-'}</td>
                          <td>
                            {version.approval_status === 'Approved' ? (
                              <span style={{ color: 'green', fontWeight: '600' }}>
                                {t('Approved')} {t('by')} {version.approver_name}
                              </span>
                            ) : version.approval_status === 'Rejected' ? (
                              <span
                                style={{ color: 'red', fontWeight: '600' }}
                                title={version.rejection_reason || ''}
                              >
                                {t('Rejected')} {t('by')} {version.approver_name}
                              </span>
                            ) : (
                              <span>{version.approval_status || '-'}</span>
                            )}
                          </td>

                          <td>{new Date(version.created_at).toLocaleString()}</td>
                          <td>
                            <div className="owner-actions owner-flex">
                              {version.email && (
                                <>
                                  <a
                                    href={`mailto:${version.email}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <DefaultemailIcon />
                                  </a>
                                </>
                              )}

                              <div style={{ marginLeft: 10 }}>
                                {version.first_name} {version.last_name}
                              </div>
                            </div>
                          </td>
                          <td className="actions">
                            {index !== 0 && (
                              <>
                                <button
                                  onClick={() =>
                                    viewVersion(version.process_id, version.level, version.version)
                                  }
                                >
                                  {t('view')}
                                </button>

                                {version.status !== 'Published' &&
                                  version.approval_status !== 'Rejected' && (
                                    <button
                                      onClick={() =>
                                        handleReplaceClick(
                                          version.level,
                                          version.process_id,
                                          version.version
                                        )
                                      }
                                    >
                                      {t('restore')}
                                    </button>
                                  )}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* TRANSLATION TAB */}
            {activeTab === 'translation' && (
              <div className="translation-tab" style={{ padding: '20px 0' }}>
                <h4 style={{ marginBottom: '15px' }}>{t('translation_management')}</h4>
                <p
                  style={{
                    marginBottom: '20px',
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.5',
                  }}
                >
                  {t('translation_tab_description')}
                </p>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  <button
                    className="popup-button"
                    style={{
                      backgroundColor: '#002060',
                      color: '#fff',
                      padding: '10px 20px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: 'none',
                    }}
                    onClick={handleDownloadTranslations}
                  >
                    {t('download_translation_report')}
                  </button>
                  <label
                    className="popup-button"
                    style={{
                      backgroundColor: '#fff',
                      color: '#002060',
                      border: '1px solid #002060',
                      padding: '9px 20px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'inline-block',
                      marginBottom: 0,
                    }}
                  >
                    {t('import_translation_report')}
                    <input
                      type="file"
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={handleImportTranslations}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* EMAIL SEARCH POPUP */}
          {showEmailPopup && (
            <div className="email-popup-overlay">
              <div className="email-popup">
                <div className="email-popup-header ">
                  <h4>
                    {t('assign')}{' '}
                    <span className="popup-role">{cleanLabel(getRoleLabel(currentRole))}</span>{' '}
                  </h4>
                </div>

                <div className="b-1">
                  <div className="pl-10 pr-10 pt-10">
                    <input
                      type="text"
                      placeholder={t('search_user')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="email-search-box"
                    />
                  </div>

                  <div className="email-list-container height-limit-300">
                    {/* 🔹 Header */}
                    <div className="email-header pl-10 pr-10">
                      <div className="email-col name">{t('name')}</div>
                      <div className="email-col email">{t('Email')}</div>
                    </div>

                    {/* 🔹 List */}
                    {filteredUsers.map((u, index) => (
                      <div
                        key={index}
                        className={`email-row pl-10 pr-10 ${tempSelectedUsers.includes(u.user.id) ? 'selected' : ''}`}
                        onClick={() => selectUser(u.user.id)}
                      >
                        <div className="email-col name">
                          {u.user.first_name} {u.user.last_name}
                        </div>
                        <div className="email-col email">{u.user.email}</div>
                      </div>
                    ))}

                    {filteredUsers.length === 0 && (
                      <p className="fs-12 mt-10 mb-0 pl-10 pr-10 mb-10">{t('no_users_found')}</p>
                    )}
                  </div>
                </div>
                <div className="mt-10 d-flex justify-end gap-10">
                  <button className="button_secondary" onClick={() => setShowEmailPopup(false)}>
                    {' '}
                    {t('Cancel')}
                  </button>
                  <button className="button_primary" onClick={handleAssignMyself}>
                    {' '}
                    {t('assign_myself')}
                  </button>
                  <button className="button_primary" onClick={handleAssign}>
                    {' '}
                    {t('assign')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showTranslationPopup && (
        <TranslationTextAreaPopup
          isOpen={showTranslationPopup}
          onClose={() => setShowTranslationPopup(false)}
          defaultValues={translations}
          onSubmit={(values) => {
            setTranslations(values);
            const langKey = langMap[selectedLanguage] || 'EN';
            setRevisionText(values[langKey]?.content || '');
            setShowTranslationPopup(false);
          }}
          supportedLanguages={supportedLanguages}
        />
      )}
    </>
  );
};

export default VersionPopup;
